import { useFormEditor } from "@/components/forms/form-editor-context.ts";
import { useFormElementUtils } from "@/components/forms/utils/useFormElementUtils.tsx";
import ScaleEditModal from "@/components/forms/widgets/ScaleEditModal.tsx";
import { useModals, LinkButtonTo, Stack } from "@skailer-com/uikit";
import { QuestionElement } from "@/components/questionnaire/QuestionElements.tsx";
import { ApiFormViewScale } from "@/types/forms/form-view.types.ts";
import { Draggable } from "@hello-pangea/dnd";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface FormElementComponentProps {
  pageIndex: number;
  sectionIndex: number;
  elementIndex: number;
}

export const FormElementComponent = ({
  pageIndex,
  sectionIndex,
  elementIndex,
}: FormElementComponentProps) => {
  const { t } = useTranslation();
  const {
    formValues,
    readOnly,
    selectedElement,
    toggleSelectedElement,
    openEditElement,
    getElementView,
    triggerFormUpdate,
  } = useFormEditor();
  const { renderEditBlock, getQuestionTitleWithSubtitle } = useFormElementUtils();
  const { openModal } = useModals();

  const element = formValues.pages[pageIndex].sections[sectionIndex].elements[elementIndex];
  const mockForQuestion = useForm();

  const isSelected =
    selectedElement &&
    selectedElement.pageIndex === pageIndex &&
    selectedElement.sectionIndex === sectionIndex &&
    selectedElement.elementIndex === elementIndex;

  const handleEditScale = useCallback((scale: ApiFormViewScale) => {
    openModal(ScaleEditModal, { formViewScale: scale, onClose: triggerFormUpdate });
  }, []);

  if (!element.id) {
    return null;
  }

  // Рендерим по view: в нём догруженные данные (например, scale), которых нет в element.
  const elementView = getElementView(element.id);
  if (!elementView) {
    return null;
  }

  return (
    <Draggable
      key={`element-${pageIndex}-${sectionIndex}-${elementIndex}`}
      draggableId={`element-${pageIndex}-${sectionIndex}-${elementIndex}`}
      index={elementIndex}
      isDragDisabled={readOnly}
    >
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <Stack direction={"horizontal"} gap={"lg"} className={"group"}>
            {renderEditBlock(pageIndex, sectionIndex, elementIndex, provided.dragHandleProps)}
            <Stack className={"flex-1 wrap-break-word overflow-hidden"}>
              <Stack
                className={"cursor-pointer "}
                onClick={() => {
                  toggleSelectedElement(pageIndex, sectionIndex, elementIndex);
                }}
              >
                {getQuestionTitleWithSubtitle(element, elementView)}
              </Stack>
              {isSelected && (
                <Stack className={"card-panel mt-4 w-full"} gap={"lg"}>
                  <FormProvider {...mockForQuestion}>
                    <QuestionElement element={elementView} />
                  </FormProvider>
                  {!readOnly && (
                    <>
                      <hr />
                      <Stack direction={"horizontal"} gap={"lg"}>
                        <LinkButtonTo
                          onClick={() => {
                            openEditElement(pageIndex, sectionIndex, elementIndex);
                          }}
                        >
                          {t("common:edit")}
                        </LinkButtonTo>
                        {elementView &&
                          elementView.scale_settings &&
                          elementView.scale_settings.form_scale && (
                            <LinkButtonTo
                              onClick={() => {
                                handleEditScale(elementView!.scale_settings!.form_scale);
                              }}
                            >
                              {t("surveys:edit_scale")}
                            </LinkButtonTo>
                          )}
                        {elementView &&
                          elementView.goals_settings &&
                          elementView.goals_settings.form_scale && (
                            <LinkButtonTo
                              onClick={() => {
                                handleEditScale(elementView!.goals_settings!.form_scale);
                              }}
                            >
                              {t("surveys:edit_scale")}
                            </LinkButtonTo>
                          )}
                      </Stack>
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </div>
      )}
    </Draggable>
  );
};
